/**
 * Marketing API Client
 */
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Create axios instance with auth
const api = axios.create({
  baseURL: `${API_BASE_URL}/api/marketing`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  return config;
});

// Types
export interface MarketUser {
  id: number;
  email: string;
  name: string;
  source: string;
  metadata?: Record<string, any>;
  is_valid: boolean;
  last_activity?: string;
  created_at: string;
  updated_at: string;
}

export interface MarketAudience {
  id: number;
  name: string;
  description: string;
  filter_json: Record<string, any>;
  is_active: boolean;
  member_count: number;
  eligible_count: number;
  member_count_cache: number;
  needs_rebuild: boolean;
  is_deletable: boolean;
  campaign_count: number;
  last_rebuilt?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface MarketCampaign {
  id: number;
  uuid: string;
  name: string;
  subject: string;
  preview_text: string;
  from_name?: string;
  from_email?: string;
  reply_to_email?: string;
  audience: number;
  audience_name: string;
  html: string;
  text?: string;
  topic?: string;
  deduplicate_by_topic: boolean;
  track_opens: boolean;
  track_clicks: boolean;
  send_rate_per_second: number;
  status: 'draft' | 'scheduled' | 'queued' | 'sending' | 'sent' | 'paused' | 'cancelled' | 'failed';
  can_send: boolean;
  progress: number;
  stats?: CampaignStats;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  opened_count: number;
  clicked_count: number;
  unsubscribed_count: number;
  scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CampaignStats {
  sent: number;
  failed: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
  open_rate: number;
  click_rate: number;
}

export interface MarketUnsubscriber {
  id: number;
  email: string;
  campaign?: number;
  campaign_name?: string;
  reason: string;
  method: string;
  is_active: boolean;
  unsubscribed_at: string;
  resubscribed_at?: string;
}

export interface CampaignSummary {
  campaign_id: number;
  campaign_name: string;
  status: string;
  total_recipients: number;
  eligible: number;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  failed: number;
  unsubscribed: number;
  open_rate: number;
  click_rate: number;
  bounce_rate: number;
}

export interface AudienceAnalytics {
  total: number;
  eligible: number;
  unsubscribed: number;
  sources: Array<{ source: string; count: number }>;
  last_rebuilt?: string;
}

// API Methods
export const marketingApi = {
  // Users/Contacts
  async getUsers(params?: {
    page?: number;
    page_size?: number;
    source?: string;
    is_valid?: boolean;
    search?: string;
  }) {
    const response = await api.get<{
      results: MarketUser[];
      count: number;
      next?: string;
      previous?: string;
    }>('/users/', { params });
    return response.data;
  },

  async createUser(data: Partial<MarketUser>) {
    const response = await api.post<MarketUser>('/users/', data);
    return response.data;
  },

  async uploadCSV(file: File, validate_emails = true, update_existing = true) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('validate_emails', String(validate_emails));
    formData.append('update_existing', String(update_existing));

    const response = await api.post<{
      success: boolean;
      message: string;
      created: number;
      updated: number;
      skipped: number;
      total: number;
      errors: string[];
    }>('/users/upload_csv/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async validateEmails() {
    const response = await api.get<{
      message: string;
      still_invalid: number;
    }>('/users/validate_emails/');
    return response.data;
  },

  // Audiences
  async getAudiences(params?: { page?: number; is_active?: boolean }) {
    const response = await api.get<{
      results: MarketAudience[];
      count: number;
    }>('/audiences/', { params });
    return response.data;
  },

  async getAudience(id: number) {
    const response = await api.get<MarketAudience>(`/audiences/${id}/`);
    return response.data;
  },

  async createAudience(data: {
    name: string;
    description?: string;
    filter_json?: Record<string, any>;
  }) {
    const response = await api.post<MarketAudience>('/audiences/', data);
    return response.data;
  },

  async updateAudience(id: number, data: Partial<MarketAudience>) {
    const response = await api.patch<MarketAudience>(`/audiences/${id}/`, data);
    return response.data;
  },

  async deleteAudience(id: number) {
    const response = await api.delete<{ message: string }>(`/audiences/${id}/`);
    return response.data;
  },

  async rebuildAudience(id: number, validate_emails = true) {
    const response = await api.post<{
      success: boolean;
      message: string;
      member_count: number;
    }>(`/audiences/${id}/rebuild_members/`, { validate_emails });
    return response.data;
  },

  async getAudienceMembers(id: number, page = 1) {
    const response = await api.get<{
      results: Array<{
        email: string;
        name?: string;
        source?: string;
        created_at?: string;
        is_valid?: boolean;
        added_at: string;
      }>;
      count: number;
    }>(`/audiences/${id}/members/`, { params: { page } });
    return response.data;
  },

  async getAudienceAnalytics(id: number) {
    const response = await api.get<AudienceAnalytics>(`/audiences/${id}/analytics/`);
    return response.data;
  },

  async createAudienceFromEmails(name: string, emails: string[]) {
    const response = await api.post<MarketAudience>('/audiences/create_from_emails/', {
      name,
      emails,
    });
    return response.data;
  },

  // Campaigns
  async getCampaigns(params?: {
    page?: number;
    status?: string;
    audience?: number;
    search?: string;
  }) {
    const response = await api.get<{
      results: MarketCampaign[];
      count: number;
    }>('/campaigns/', { params });
    return response.data;
  },

  async getCampaign(id: number) {
    const response = await api.get<MarketCampaign>(`/campaigns/${id}/`);
    return response.data;
  },

  async createCampaign(data: {
    name: string;
    subject: string;
    preview_text?: string;
    from_name?: string;
    from_email?: string;
    reply_to_email?: string;
    audience: number;
    html: string;
    text?: string;
    track_opens?: boolean;
    track_clicks?: boolean;
    send_rate_per_second?: number;
    scheduled_at?: string;
  }) {
    const response = await api.post<MarketCampaign>('/campaigns/', data);
    return response.data;
  },

  async updateCampaign(id: number, data: Partial<MarketCampaign>) {
    const response = await api.patch<MarketCampaign>(`/campaigns/${id}/`, data);
    return response.data;
  },

  async deleteCampaign(id: number) {
    const response = await api.delete<{ message: string }>(`/campaigns/${id}/`);
    return response.data;
  },

  async sendCampaign(id: number, test_mode = false, test_emails?: string[]) {
    const response = await api.post<{
      success: boolean;
      message: string;
      sent?: number;
      failed?: number;
      total?: number;
      error?: string;
    }>(`/campaigns/${id}/send/`, {
      test_mode,
      test_emails,
    });
    return response.data;
  },

  async pauseCampaign(id: number) {
    const response = await api.post<{ message: string }>(`/campaigns/${id}/pause/`);
    return response.data;
  },

  async resumeCampaign(id: number) {
    const response = await api.post<{ message: string }>(`/campaigns/${id}/resume/`);
    return response.data;
  },

  async getCampaignSummary(id: number) {
    const response = await api.get<CampaignSummary>(`/campaigns/${id}/summary/`);
    return response.data;
  },

  async getCampaignSends(id: number, params?: {
    page?: number;
    email?: string;
    status?: string;
  }) {
    const response = await api.get<{
      results: Array<{
        id: number;
        email: string;
        status: string;
        sent_at?: string;
        opened_at?: string;
        clicked_at?: string;
      }>;
      count: number;
    }>(`/campaigns/${id}/sends/`, { params });
    return response.data;
  },

  async previewCampaign(id: number, email?: string, name?: string) {
    const response = await api.get<{
      subject: string;
      html: string;
      from: string;
      reply_to?: string;
    }>(`/campaigns/${id}/preview/`, {
      params: { email, name },
    });
    return response.data;
  },

  async estimateSendTime(id: number) {
    const response = await api.get<{
      total_recipients: number;
      rate_limit: number;
      estimated_seconds: number;
      estimated_minutes: number;
      batch_size: number;
      num_batches: number;
    }>(`/campaigns/${id}/estimate_time/`);
    return response.data;
  },

  // Unsubscribers
  async getUnsubscribers(params?: {
    page?: number;
    is_active?: boolean;
    reason?: string;
    search?: string;
  }) {
    const response = await api.get<{
      results: MarketUnsubscriber[];
      count: number;
    }>('/unsubscribers/', { params });
    return response.data;
  },

  async resubscribe(email: string) {
    const response = await api.post<{ message: string }>('/unsubscribers/resubscribe/', {
      email,
    });
    return response.data;
  },
};

export default marketingApi;